/**
 * TodayStatusCardV2View — "금일 출하 현황" dense 카드(today2)의 순수 표시 컴포넌트.
 *
 * shipments(원본 RqcShipment[])와 부제 텍스트를 prop 으로 받아 요약·상태칩·마지막출하를
 * 자체 계산한다. 데이터 fetch 는 호출측 책임.
 *  - 홈 대시보드(TodayStatusCardV2): useRqcShipments(kstToday()) 의 shipments 주입
 *  - 출하조회 MO(dispatch-today-mo): 선택 날짜(selectedDate) 의 shipments 주입
 *
 * CSS 클래스(.today2 / .today2-stats / .today2-edge …)는 components.css 공용 정의를
 * 그대로 사용 → 두 화면이 동일 디자인 유지. 하단 진행률바(.today2-edge)는 부모
 * .widget-tile 기준 absolute 이므로, 이 카드는 .widget-tile.is-card > .tile-inner 안에
 * 배치되어야 한다(홈과 동일 프레임).
 */
import type { ReactNode } from "react";
import type { RqcShipment } from "@/domain/rqc";
import type { LastShipment } from "@/hooks/useLastShipment";
import { fmt, summarize } from "./lib";
import { extractLastcarTime } from "./timelineUtils";

const STATUS_EPS = 0.05;

interface Props {
  /** 원본 출하 행 (상태칩·마지막출하 계산에 필요) */
  shipments: RqcShipment[];
  /** 카드 부제 — 예: "6월 18일 (목) · 실시간" */
  sub: string;
  /** 마지막 출하 1건(시각 + 차량번호) — 송장 명세 캐시 기반. 없으면 lastcar 시각만 폴백 */
  lastShipment?: LastShipment | null;
  /**
   * tile   — 위젯 한 장을 채우는 기본형: 상태칩이 헤더 우측, 진행률바는 카드 하단 모서리(absolute)
   * inline — 다른 카드 안에 끼워 넣는 형(홈 MO "금일 출하" 통합 카드):
   *          상태칩은 보조줄 우측으로 내려가고, 진행률바는 흐름 안의 막대가 된다
   */
  layout?: "tile" | "inline";
  /** 헤더 우측 슬롯 (inline 에서만) — 예: 최근 업데이트 시각 + 새로고침 버튼 */
  actions?: ReactNode;
  /** 카드 제목. 기본 "금일 출하 현황" (내 업무 홈 카드는 "출하 현황") */
  title?: string;
  /** 제목 바로 옆에 붙는 칩 — 예: 내 업무 홈의 법인 칩(중앙/경원) */
  titleAddon?: ReactNode;
  /** 물량 단위. 기본 "㎥" (경원 아스콘은 "톤") */
  unit?: string;
  /** 계획 수량 칸 이름. 기본 "예정" (경원 아스콘은 "수주") */
  planLabel?: string;
}

export function TodayStatusCardV2View({
  shipments, sub, lastShipment, layout = "tile", actions, title = "금일 출하 현황", titleAddon, unit = "㎥", planLabel = "예정",
}: Props) {
  const inline = layout === "inline";
  const s = summarize(shipments);
  let done = 0, moving = 0, waiting = 0;
  for (const r of shipments) {
    if (r.shipped > 0 && r.remain <= STATUS_EPS) done += 1;
    else if (r.shipped > 0) moving += 1;
    else waiting += 1;
  }
  const planned = s.qtyTotal;
  const shipped = s.shipTotal;
  const remain = s.remainTotal;
  const pct = planned > 0 ? (shipped / planned) * 100 : 0;
  // 마지막 출하 시각 = 전 현장 lastcar(HHMM) 중 최대값
  let lastMin = -1;
  for (const sh of shipments) {
    const t = extractLastcarTime(sh.lastcar);
    if (t != null && t > lastMin) lastMin = t;
  }
  // 마지막 출하 시각: 송장 명세(차번 포함) 우선, 없으면 lastcar 폴백
  const lastTime = lastShipment?.time
    ?? (lastMin >= 0
      ? `${String(Math.floor(lastMin / 60)).padStart(2, "0")}:${String(lastMin % 60).padStart(2, "0")}`
      : "—");

  const chips = (
    <div className="today2-chips">
      <span className="today2-chip is-done">완료 {done}</span>
      <span className="today2-chip is-moving">진행 {moving}</span>
      <span className="today2-chip is-wait">대기 {waiting}</span>
    </div>
  );

  return (
    <div className={`today2${inline ? " is-inline" : ""}`}>
      <div className="card-hd chart-card-hd today2-top">
        <div className="chart-card-hd-text">
          {titleAddon ? (
            <div className="today2-title-row">
              <div className="card-title">{title}</div>
              {titleAddon}
            </div>
          ) : (
            <div className="card-title">{title}</div>
          )}
          <div className="card-sub">{sub}</div>
        </div>
        {inline ? actions : chips}
      </div>
      <div className="today2-stats">
        <div className="today2-stat">
          <div className="today2-stat-lbl">{planLabel}</div>
          <div className="today2-stat-val">{fmt(planned, 0)}<i>{unit}</i></div>
        </div>
        <span className="today2-stat-sep" />
        <div className="today2-stat is-accent">
          <div className="today2-stat-lbl">출하</div>
          <div className="today2-stat-val">{fmt(shipped, 0)}<i>{unit}</i></div>
        </div>
        <span className="today2-stat-sep" />
        <div className="today2-stat">
          <div className="today2-stat-lbl">잔량</div>
          <div className="today2-stat-val">{fmt(remain, 0)}<i>{unit}</i></div>
        </div>
        <span className="today2-stat-sep" />
        <div className="today2-stat">
          <div className="today2-stat-lbl">진행률</div>
          <div className="today2-stat-val">{pct.toFixed(0)}<i>%</i></div>
        </div>
      </div>
      <div className="today2-sub">
        <span>관급 <b>{s.cntGov}</b> · 사급 <b>{s.cntCust}</b></span>
        <span className="today2-sub-dot" />
        <span>탕수 <b>{s.carsTotal}</b>회</span>
        <span className="today2-sub-dot" />
        <span>
          막차 <b>{lastTime}</b> ·
          {lastShipment?.carNo ? <b className="today2-carno">{lastShipment.carNo}</b> : null}
        </span>
        {inline ? chips : null}
      </div>
      {inline ? (
        <div className="today2-inline-bar" title={`출하 진행률 ${pct.toFixed(1)}%`}>
          <div className="today2-inline-fill" style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      ) : (
        <div className="today2-edge" title={`출하 진행률 ${pct.toFixed(1)}%`}>
          <div className="today2-edge-fill" style={{ width: `${Math.min(100, pct)}%` }} />
          <span className="today2-edge-pct">진행률 {pct.toFixed(0)}%</span>
        </div>
      )}
    </div>
  );
}
